import React from 'react';
import { useIntl } from 'react-intl';
// Deep imports rather than the `src/components` barrel — see CompareSlider.
import Button from 'src/components/Button';
import Input from 'src/components/Input';
import messages from '../messages';
import styles from './SaveResultForm.module.scss';

export type SaveDisposition = 'conversion' | 'new_asset';

/** Why a name was rejected. Pure so it can be unit-tested and reused. */
export type NameProblem = 'required' | 'tooLong' | 'invalid';

/** Base name length cap. For a filename this excludes the ".ext". */
const MAX_BASE_LENGTH = 20;
const CONVERSION_PATTERN = /^[a-zA-Z0-9_-]+$/;
const FILENAME_PATTERN = /^[a-zA-Z0-9_-]+(\.[a-zA-Z0-9]+)?$/;

/**
 * Validate a conversion name or a new-asset filename.
 *
 * Returns `null` when the name is fine, otherwise which rule it broke.
 * `allowExtension` marks a filename: the length cap then applies to the base
 * name so there is room for a trailing ".ext".
 */
export function validateResultName(name: string, allowExtension = false): NameProblem | null {
  if (!name) return 'required';
  const base = allowExtension ? name.replace(/\.[^.]+$/, '') : name;
  if (base.length > MAX_BASE_LENGTH) return 'tooLong';
  if (!(allowExtension ? FILENAME_PATTERN : CONVERSION_PATTERN).test(name)) return 'invalid';
  return null;
}

const PROBLEM_MESSAGES: Record<NameProblem, keyof typeof messages> = {
  required: 'aiEditNameRequired',
  tooLong: 'aiEditNameTooLong',
  invalid: 'aiEditNameInvalid',
};

export type SaveResultFormProps = {
  disposition: SaveDisposition;
  value: string;
  onChange: (value: string) => void;
  onBack: () => void;
  onConfirm: () => void;
  /** Set by the caller after `validateResultName` fails. */
  problem?: NameProblem | null;
  confirming?: boolean;
  /**
   * Access for the saved result. Optional: when `onAccessChange` is omitted the
   * control is not rendered, so existing callers are unaffected.
   *
   * Rendered for a **conversion only** — a conversion is what gets served
   * through the dynamic-sizing API (`/conversions`), so it is the thing worth
   * publishing. A new asset's original is always private server-side
   * (`_register_new_asset` forces it), so a toggle there would be a control
   * that quietly does nothing.
   */
  isPublic?: boolean;
  onAccessChange?: (isPublic: boolean) => void;
  /**
   * Whether a new asset inherits the source's metadata. Rendered for a **new
   * asset only** — a conversion belongs to the asset whose metadata it already
   * shares, so there is nothing to inherit.
   */
  inheritMetadata?: boolean;
  onInheritMetadataChange?: (inherit: boolean) => void;
};

/**
 * Name-and-confirm step for a staged result, shown in the action bar.
 *
 * Extracted from AIImageEditDialog so the cut-out panel saves its result the
 * same way — same rules, same copy, same shape.
 */
export const SaveResultForm: React.FC<SaveResultFormProps> = ({
  disposition,
  value,
  onChange,
  onBack,
  onConfirm,
  problem,
  confirming,
  isPublic,
  onAccessChange,
  inheritMetadata,
  onInheritMetadataChange,
}) => {
  const { formatMessage } = useIntl();
  const isConversion = disposition === 'conversion';

  return (
    <div className={styles.saveForm}>
      <Input
        type="text"
        variant="secondary"
        value={value}
        onChange={onChange}
        placeholder={formatMessage(
          isConversion ? messages.aiEditConversionPlaceholder : messages.aiEditNewAssetPlaceholder,
        )}
        label={formatMessage(isConversion ? messages.aiEditConversionName : messages.aiEditNewAssetName)}
        error={problem ? formatMessage(messages[PROBLEM_MESSAGES[problem]]) : undefined}
      />
      {!isConversion && <span className={styles.hint}>{formatMessage(messages.aiEditNoExtensionNote)}</span>}
      {!isConversion && onInheritMetadataChange && (
        <div className={styles.access}>
          <label className={styles.inherit}>
            <input
              type="checkbox"
              checked={!!inheritMetadata}
              disabled={confirming}
              onChange={(e) => onInheritMetadataChange(e.target.checked)}
              data-test="save-inherit-metadata"
            />
            {formatMessage(messages.aiEditInheritMetadata)}
          </label>
          <span className={styles.hint}>{formatMessage(messages.aiEditInheritMetadataHint)}</span>
        </div>
      )}
      {isConversion && onAccessChange && (
        <div className={styles.access}>
          <span className={styles.accessLabel}>{formatMessage(messages.cutoutAccessLabel)}</span>
          <div className={styles.accessOptions}>
            <button
              type="button"
              className={isPublic ? `${styles.accessOption} ${styles.accessOptionOn}` : styles.accessOption}
              aria-pressed={!!isPublic}
              onClick={() => onAccessChange(true)}
              disabled={confirming}
              data-test="save-access-public"
            >
              {formatMessage(messages.cutoutAccessPublic)}
            </button>
            <button
              type="button"
              className={!isPublic ? `${styles.accessOption} ${styles.accessOptionOn}` : styles.accessOption}
              aria-pressed={!isPublic}
              onClick={() => onAccessChange(false)}
              disabled={confirming}
              data-test="save-access-private"
            >
              {formatMessage(messages.cutoutAccessPrivate)}
            </button>
          </div>
          <span className={styles.hint}>
            {formatMessage(
              isPublic ? messages.cutoutAccessPublicHint : messages.cutoutAccessPrivateHint,
            )}
          </span>
        </div>
      )}
      <div className={styles.saveFormActions}>
        <Button mode="flat" size="small" onClick={onBack}>
          {formatMessage(messages.goBack)}
        </Button>
        <Button mode="primary" size="small" onClick={onConfirm} loading={confirming}>
          {formatMessage(messages.aiEditConfirm)}
        </Button>
      </div>
    </div>
  );
};

export default SaveResultForm;
